// SajiloHealth — bilingual copy (EN + NE). Tight, visual-first.

export const copy = {
  en: {
    nav: { platform: "Platform", why: "Why us", investors: "Investors", demo: "Book a demo", forInvestors: "For investors" },
    langLabel: "Language",

    hero: {
      eyebrow: "Nepal's first AI-native cloud HCMS",
      line1: "The hospital & clinic management system —",
      phrases: ["All in the cloud.", "OPD, IPD, pharmacy, labs.", "For hospitals & clinics.", "AI inside the EMR.", "One live record."],
      srCompletion: "all in the cloud.",
      sub: "A complete Hospital & Clinic Management System in the cloud — OPD, IPD, wards, pharmacy, labs, billing and EMR, unified and AI-native.",
      chips: ["OPD to IPD, one system", "Cloud — access anywhere", "AI-native EMR"],
      cloudTag: "Cloud-based · access anywhere",
      beta: { days: "days to private beta", plan: "Planned · Oct 20" },
    },

    scale: {
      label: "Built to scale",
      items: ["OPD", "IPD", "Pharmacy", "Labs", "Billing", "QR payments", "Multi-branch", "Bed management", "Departments", "Analytics"],
    },

    problem: {
      kicker: "The problem",
      h2: "Clinics and hospitals still run on paper.",
      sub: "Registers, spreadsheets and phone calls — and none of it reaches the patient.",
      receiptHead: "The old way → one system",
      rows: [
        { old: "Register book — OPD", to: "OPD queue" },
        { old: "Excel — pharmacy", to: "Inventory" },
        { old: "Paper — lab slip", to: "Lab & diagnostics" },
        { old: "Cash ledger", to: "Billing + QR" },
        { old: "“Old prescription?”", to: "Records in the cloud" },
      ],
    },

    unified: {
      kicker: "The platform",
      h2: "One system. Every department.",
      sub: "From OPD and wards to pharmacy, lab and billing — every role on one live record.",
      cloud: "Every record lives in the cloud — open it from the front desk, a ward, or a phone.",
      roles: ["Patients", "Doctors", "Reception", "Pharmacy", "Lab", "Admin"],
      hub: "One system · one login · one record — in the cloud",
      modules: ["Appointments", "OPD", "IPD", "EMR", "Pharmacy", "Labs", "Radiology", "Billing", "Insurance", "Wards & beds", "Inventory", "Reports", "QR payments", "AI notes"],
      same: "Everyone works in the same app",
    },

    journey: {
      kicker: "One record, one system",
      h2: "One visit, end to end.",
      sub: "No exports, no separate apps, no re-entry — booking to payment, all in the cloud.",
      persistent: "One app · one live record · in the cloud",
      steps: [
        { tag: "Appointments", title: "Booked in the app", cap: "The patient books; your desk sees it live." },
        { tag: "EMR · Video", title: "Same live record", cap: "In-person or video — one chart." },
        { tag: "AI · first in Nepal", title: "AI writes the notes", cap: "Structured notes, summaries, safety flags." },
        { tag: "Pharmacy", title: "Sent to pharmacy", cap: "Stock and dispensing update themselves." },
        { tag: "Billing · Cloud", title: "Paid by QR", cap: "Invoice closes; record saved to the cloud." },
      ],
    },

    difference: {
      badge: "Nepal-first · AI-native · Cloud",
      h2: "Not like anything Nepal has had.",
      cards: [
        { t: "A complete HCMS", b: "OPD, IPD, wards, pharmacy, lab, billing and EMR — one system, not point tools." },
        { t: "Cloud-native", b: "The same live chart from front desk, ward, or phone." },
        { t: "AI in the EMR", b: "The first EMR in Nepal with AI built in.", badge: "First in Nepal" },
      ],
    },

    why: {
      kicker: "Why Nepal",
      h2: "Built for Nepal, not adapted.",
      cards: [
        { l: "Data residency", h: "MoCIT-ready", b: "Local hosting for sensitive data." },
        { l: "Connectivity", h: "Works on real networks", b: "Usable well beyond Kathmandu speeds." },
        { l: "Payments", h: "eSewa · Khalti · QR", b: "Rails patients already use." },
        { l: "Language", h: "Nepali, natively", b: "Bilingual Nepali / English." },
      ],
    },

    investors: {
      kicker: "For investors",
      h2: "Why now.",
      thesis: [
        { l: "A complete cloud HCMS — clinic to hospital.", b: "OPD, IPD, pharmacy, lab, billing and EMR in one system — one vendor across the whole operation." },
        { l: "AI-native from the record up.", b: "First-mover on the layer that compounds with data." },
        { l: "Data + built-in distribution.", b: "One live dataset; every clinic brings its patients." },
        { l: "Nepal-first, not Nepal-only.", b: "Groundwork for underserved South Asian markets." },
      ],
      tractionLabel: "Traction",
      statLabels: {
        clinics: "Clinics & hospitals live",
        patients: "Patients registered",
        transactions: "Transactions",
        daysToBeta: "Days to private beta",
      },
      cta: "Request the investor deck",
      note: "Verified figures available on request.",
    },

    customers: {
      kicker: "For hospitals & clinics",
      h2: "Live in days, not months.",
      sub: "From a multi-department hospital to a single clinic — the same cloud HCMS.",
      cards: [
        { eyebrow: "Hospitals & networks", h: "Runs the whole hospital", b: "OPD, IPD, wards, OT, pharmacy, lab, multi-branch billing, insurance and analytics.", cta: "Talk to our team" },
        { eyebrow: "Clinics & polyclinics", h: "Up and running in days", b: "The full system, right-sized — with your patients on the app from day one.", cta: "Book a demo" },
      ],
    },

    demo: {
      kicker: "Book a demo",
      h2: "See it running on your day.",
      sub: "A walkthrough of the system, mapped to how your clinic or hospital works.",
      note: "Clinics · hospitals · investors",
      name: "Full name",
      namePh: "Dr. Anjali Rai",
      org: "Clinic / hospital",
      orgPh: "Lakeside Hospital",
      email: "Work or personal email",
      emailPh: "you@domain.com",
      roleLabel: "I am a",
      roles: [
        { id: "clinic", label: "Clinic" },
        { id: "hospital", label: "Hospital" },
        { id: "doctor", label: "Doctor" },
        { id: "investor", label: "Investor" },
      ],
      submit: "Request a demo",
      submitting: "Sending…",
      success: "Request received",
      successDesc: "Thank you — we'll reach out to schedule your walkthrough.",
      another: "← Submit another",
      privacy: "We never share your details.",
      errEmail: "Please enter a valid email address.",
      errRole: "Please choose what describes you best.",
      errVerify: "Please complete the verification.",
      errNetwork: "Could not reach our servers. Please try again.",
      errGeneric: "Something went wrong. Please try again.",
    },

    footer: {
      blurb: "The complete, AI-native, cloud Hospital & Clinic Management System — built for Nepal.",
      product: "Product",
      company: "Company",
      reach: "Reach us",
      links: { platform: "Platform", how: "How it works", why: "Why Nepal", investors: "Investors", demo: "Book a demo", contact: "Contact" },
      rights: "All rights reserved.",
      registered: "Registered under Nepal OCR CAMIS",
      privacy: "Privacy",
      terms: "Terms",
      cookies: "Cookies",
    },
  },

  ne: {
    nav: { platform: "प्लेटफर्म", why: "किन हामी", investors: "लगानीकर्ता", demo: "डेमो बुक", forInvestors: "लगानीकर्ताका लागि" },
    langLabel: "भाषा",

    hero: {
      eyebrow: "नेपालको पहिलो एआई-नेटिभ क्लाउड HCMS",
      line1: "अस्पताल र क्लिनिक व्यवस्थापन प्रणाली —",
      phrases: ["सबै क्लाउडमा।", "OPD, IPD, फार्मेसी, ल्याब।", "अस्पताल र क्लिनिकका लागि।", "EMR भित्रै एआई।", "एउटै लाइभ रेकर्ड।"],
      srCompletion: "सबै क्लाउडमा।",
      sub: "क्लाउडमा सम्पूर्ण अस्पताल र क्लिनिक व्यवस्थापन प्रणाली — OPD, IPD, वार्ड, फार्मेसी, ल्याब, बिलिङ र EMR, एकीकृत र एआई-नेटिभ।",
      chips: ["OPD देखि IPD, एउटै प्रणाली", "क्लाउड — जहाँबाट पनि", "एआई-नेटिभ EMR"],
      cloudTag: "क्लाउडमा · जहाँबाट पनि पहुँच",
      beta: { days: "दिन · निजी बिटासम्म", plan: "योजना · अक्टोबर २०" },
    },

    scale: {
      label: "स्केलका लागि निर्मित",
      items: ["OPD", "IPD", "फार्मेसी", "ल्याब", "बिलिङ", "QR भुक्तानी", "मल्टि-ब्रान्च", "बेड व्यवस्थापन", "विभागहरू", "एनालिटिक्स"],
    },

    problem: {
      kicker: "समस्या",
      h2: "क्लिनिक र अस्पताल अझै कागजमै चल्छन्।",
      sub: "रजिस्टर, स्प्रेडसिट र फोन कल — र केही पनि बिरामीसम्म पुग्दैन।",
      receiptHead: "पुरानो तरिका → एउटै प्रणाली",
      rows: [
        { old: "रजिस्टर — OPD", to: "OPD लाइन" },
        { old: "एक्सेल — फार्मेसी", to: "इन्भेन्टरी" },
        { old: "कागज — ल्याब स्लिप", to: "ल्याब र डायग्नोस्टिक्स" },
        { old: "नगद खाता", to: "बिलिङ + QR" },
        { old: "“पुरानो पर्ची?”", to: "क्लाउडमा रेकर्ड" },
      ],
    },

    unified: {
      kicker: "प्लेटफर्म",
      h2: "एउटै प्रणाली। हरेक विभाग।",
      sub: "OPD र वार्डदेखि फार्मेसी, ल्याब र बिलिङसम्म — हरेक भूमिका एउटै लाइभ रेकर्डमा।",
      cloud: "हरेक रेकर्ड क्लाउडमा — फ्रन्ट डेस्क, वार्ड वा फोनबाट खोल्नुहोस्।",
      roles: ["बिरामी", "डाक्टर", "रिसेप्सन", "फार्मेसी", "ल्याब", "एड्मिन"],
      hub: "एउटै प्रणाली · एउटै लगिन · एउटै रेकर्ड — क्लाउडमा",
      modules: ["अपोइन्टमेन्ट", "OPD", "IPD", "EMR", "फार्मेसी", "ल्याब", "रेडियोलोजी", "बिलिङ", "बीमा", "वार्ड र बेड", "इन्भेन्टरी", "रिपोर्ट", "QR भुक्तानी", "एआई नोट"],
      same: "सबैले उही एपमा काम गर्छन्",
    },

    journey: {
      kicker: "एउटै रेकर्ड, एउटै प्रणाली",
      h2: "एउटै भिजिट, सुरुदेखि अन्त्यसम्म।",
      sub: "न एक्सपोर्ट, न छुट्टै एप, न पुनः प्रविष्टि — बुकिङदेखि भुक्तानीसम्म, सबै क्लाउडमा।",
      persistent: "एउटै एप · एउटै लाइभ रेकर्ड · क्लाउडमा",
      steps: [
        { tag: "अपोइन्टमेन्ट", title: "एपमा बुक", cap: "बिरामीले बुक गर्छन्; डेस्कले तुरुन्तै देख्छ।" },
        { tag: "EMR · भिडियो", title: "उही लाइभ रेकर्ड", cap: "प्रत्यक्ष वा भिडियो — एउटै चार्ट।" },
        { tag: "एआई · नेपालमा पहिलो", title: "एआईले नोट लेख्छ", cap: "संरचित नोट, सारांश, सुरक्षा फ्ल्याग।" },
        { tag: "फार्मेसी", title: "फार्मेसीमा पठाइयो", cap: "स्टक र वितरण आफैँ अपडेट हुन्छ।" },
        { tag: "बिलिङ · क्लाउड", title: "QR बाट भुक्तानी", cap: "इनभ्वाइस बन्द; रेकर्ड क्लाउडमा सुरक्षित।" },
      ],
    },

    difference: {
      badge: "नेपालमा पहिलो · एआई-नेटिभ · क्लाउड",
      h2: "नेपालले नपाएको किसिमको।",
      cards: [
        { t: "सम्पूर्ण HCMS", b: "OPD, IPD, वार्ड, फार्मेसी, ल्याब, बिलिङ र EMR — एउटै प्रणाली, छरिएका उपकरण होइन।" },
        { t: "क्लाउड-नेटिभ", b: "फ्रन्ट डेस्क, वार्ड वा फोनबाट उही लाइभ चार्ट।" },
        { t: "EMR मा एआई", b: "एआई भित्रै भएको नेपालको पहिलो EMR।", badge: "नेपालमा पहिलो" },
      ],
    },

    why: {
      kicker: "किन नेपाल",
      h2: "नेपालका लागि निर्मित, अनुकूलित होइन।",
      cards: [
        { l: "डाटा रेसिडेन्सी", h: "MoCIT-तयार", b: "संवेदनशील डाटाको स्थानीय होस्टिङ।" },
        { l: "कनेक्टिभिटी", h: "वास्तविक नेटवर्कमा", b: "काठमाडौं बाहिर पनि सजिलै चल्ने।" },
        { l: "भुक्तानी", h: "eSewa · Khalti · QR", b: "बिरामीले चलाइरहेकै माध्यम।" },
        { l: "भाषा", h: "नेपाली, नेटिभ", b: "द्विभाषिक नेपाली / अंग्रेजी।" },
      ],
    },

    investors: {
      kicker: "लगानीकर्ताका लागि",
      h2: "किन अहिले।",
      thesis: [
        { l: "सम्पूर्ण क्लाउड HCMS — क्लिनिकदेखि अस्पताल।", b: "OPD, IPD, फार्मेसी, ल्याब, बिलिङ र EMR एउटै प्रणालीमा — पूरै सञ्चालनमा एउटै भेन्डर।" },
        { l: "रेकर्डदेखि नै एआई-नेटिभ।", b: "डाटासँगै बढ्दै जाने तहमा अग्रगामी।" },
        { l: "डाटा + निर्मित वितरण।", b: "एउटै लाइभ डाटासेट; हरेक क्लिनिकले बिरामी ल्याउँछ।" },
        { l: "नेपाल-पहिलो, नेपाल-मात्र होइन।", b: "दक्षिण एसियाली बजारका लागि आधार।" },
      ],
      tractionLabel: "ट्र्याक्सन",
      statLabels: {
        clinics: "क्लिनिक र अस्पताल सक्रिय",
        patients: "दर्ता बिरामी",
        transactions: "कारोबार",
        daysToBeta: "निजी बिटासम्म दिन",
      },
      cta: "इन्भेस्टर डेक अनुरोध गर्नुहोस्",
      note: "प्रमाणित अंक अनुरोधमा उपलब्ध।",
    },

    customers: {
      kicker: "अस्पताल र क्लिनिकका लागि",
      h2: "महिना होइन, दिनमै सुरु।",
      sub: "बहु-विभाग अस्पतालदेखि एउटै क्लिनिकसम्म — उही क्लाउड HCMS।",
      cards: [
        { eyebrow: "अस्पताल र नेटवर्क", h: "पूरै अस्पताल चलाउँछ", b: "OPD, IPD, वार्ड, OT, फार्मेसी, ल्याब, मल्टि-ब्रान्च बिलिङ, बीमा र एनालिटिक्स।", cta: "टोलीसँग कुरा गर्नुहोस्" },
        { eyebrow: "क्लिनिक र पोलिक्लिनिक", h: "दिनमै सञ्चालनमा", b: "पूर्ण प्रणाली, उपयुक्त आकारमा — पहिलो दिनदेखि बिरामी एपमा।", cta: "डेमो बुक" },
      ],
    },

    demo: {
      kicker: "डेमो बुक गर्नुहोस्",
      h2: "आफ्नै दिनमा चलेको हेर्नुहोस्।",
      sub: "तपाईंको क्लिनिक वा अस्पताल कसरी चल्छ, त्यसैअनुसार प्रणालीको वाकथ्रु।",
      note: "क्लिनिक · अस्पताल · लगानीकर्ता",
      name: "पूरा नाम",
      namePh: "डा. अन्जली राई",
      org: "क्लिनिक / अस्पताल",
      orgPh: "लेकसाइड अस्पताल",
      email: "कार्य वा व्यक्तिगत इमेल",
      emailPh: "you@domain.com",
      roleLabel: "म हुँ",
      roles: [
        { id: "clinic", label: "क्लिनिक" },
        { id: "hospital", label: "अस्पताल" },
        { id: "doctor", label: "डाक्टर" },
        { id: "investor", label: "लगानीकर्ता" },
      ],
      submit: "डेमो अनुरोध",
      submitting: "पठाउँदै…",
      success: "अनुरोध प्राप्त भयो",
      successDesc: "धन्यवाद — वाकथ्रु मिलाउन हामी सम्पर्क गर्नेछौं।",
      another: "← अर्को पठाउनुहोस्",
      privacy: "हामी तपाईंको विवरण साझा गर्दैनौं।",
      errEmail: "कृपया मान्य इमेल ठेगाना प्रविष्ट गर्नुहोस्।",
      errRole: "कृपया तपाईंलाई के राम्रो वर्णन गर्छ छान्नुहोस्।",
      errVerify: "कृपया प्रमाणीकरण पूरा गर्नुहोस्।",
      errNetwork: "हाम्रो सर्भरमा पुग्न सकिएन। कृपया फेरि प्रयास गर्नुहोस्।",
      errGeneric: "केही गडबड भयो। कृपया फेरि प्रयास गर्नुहोस्।",
    },

    footer: {
      blurb: "नेपालका लागि निर्मित सम्पूर्ण, एआई-नेटिभ, क्लाउड अस्पताल र क्लिनिक व्यवस्थापन प्रणाली।",
      product: "उत्पादन",
      company: "कम्पनी",
      reach: "सम्पर्क",
      links: { platform: "प्लेटफर्म", how: "कसरी काम गर्छ", why: "किन नेपाल", investors: "लगानीकर्ता", demo: "डेमो बुक", contact: "सम्पर्क" },
      rights: "सर्वाधिकार सुरक्षित।",
      registered: "नेपाल OCR CAMIS मा दर्ता",
      privacy: "गोपनीयता",
      terms: "सर्तहरू",
      cookies: "कुकिज",
    },
  },
};
